import { isSelfServiceUserType, SelfServiceUserType } from '@/layers/support/authz/userType';

/**
 * The account type picked at sign-up, kept per email until the first sign-in sets it.
 * `localStorage` is shared by every tab of the origin, so a confirmation in another tab
 * still finds it; another device does not, and AuthContext asks the user instead.
 */
const PENDING_USER_TYPE_KEY = 'r2h_pending_user_type';

/** Matches Cognito's sign-up code lifetime, so a shared browser does not keep the email for long. */
const PENDING_USER_TYPE_TTL_MS = 24 * 60 * 60 * 1000;

type PendingUserTypes = Record<string, { type: unknown; savedAt: number }>;

const normalise = (email: string) => email.trim().toLowerCase();

function readAll(): PendingUserTypes {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(PENDING_USER_TYPE_KEY) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const oldest = Date.now() - PENDING_USER_TYPE_TTL_MS;
    const fresh: PendingUserTypes = {};
    for (const [email, entry] of Object.entries(parsed as Record<string, unknown>)) {
      const savedAt = (entry as { savedAt?: unknown } | null)?.savedAt;
      if (typeof savedAt === 'number' && savedAt > oldest) {
        fresh[email] = { type: (entry as { type?: unknown }).type, savedAt };
      }
    }
    return fresh;
  } catch {
    return {};
  }
}

function writeAll(pending: PendingUserTypes) {
  try {
    if (Object.keys(pending).length === 0) {
      window.localStorage.removeItem(PENDING_USER_TYPE_KEY);
    } else {
      window.localStorage.setItem(PENDING_USER_TYPE_KEY, JSON.stringify(pending));
    }
  } catch {
    // Storage is unavailable (private mode, quota); AuthContext asks the user instead.
  }
}

export function rememberPendingUserType(email: string, type: SelfServiceUserType) {
  writeAll({ ...readAll(), [normalise(email)]: { type, savedAt: Date.now() } });
}

export function readPendingUserType(email: string | undefined): SelfServiceUserType | null {
  if (!email) return null;
  const type = readAll()[normalise(email)]?.type;
  return isSelfServiceUserType(type) ? type : null;
}

/** Called on sign-out, so a shared browser keeps no one's sign-up email. */
export function forgetAllPendingUserTypes() {
  writeAll({});
}

export function forgetPendingUserType(email: string | undefined) {
  if (!email) return;
  const pending = readAll();
  const key = normalise(email);
  if (!Object.prototype.hasOwnProperty.call(pending, key)) return;
  delete pending[key];
  writeAll(pending);
}
